/**
 * DISCLOSURES LIBRARY. UF-00 section 6.4, and UF-07 section 4.6.
 *
 * The persistent sandbox banner in the top bar links here, from every screen. Until now that link
 * went nowhere. This is the page it points at: everything the participant has been asked to agree
 * to, in plain language, with the version and the date they acknowledged it, so they can always
 * re-read what they signed up for (FR-U-DSP-004).
 *
 * Acknowledgement is answered by the server from the append-only records, never inferred here. A
 * disclosure whose current version the participant has NOT acknowledged is flagged, which is the
 * hook the re-acknowledgement flow (FR-X-DSP-004) hangs on when a disclosure is revised.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, LipaApiError, type Disclosure } from '@/lib/api';
import { useSession } from '@/lib/session-context';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';

export default function DisclosuresPage() {
  const { me } = useSession();
  const [items, setItems] = useState<Disclosure[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!me) return;
    setError(null);
    api
      .disclosures(me.participant_id)
      .then(setItems)
      .catch((e) =>
        setError(e instanceof LipaApiError ? e.message : 'We could not load the disclosures.'),
      );
  }, [me]);

  useEffect(load, [load]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-ink">Disclosures</h1>
        <p className="mt-1 text-sm text-muted-ink">
          Everything you have been asked to agree to, and when you agreed to it. VASP runs in
          the Capital Markets Authority regulatory sandbox, an experimental supervised environment.
        </p>
      </header>

      {error ? (
        <ErrorState body={error} onRetry={load} />
      ) : items === null ? (
        <LoadingCard lines={4} />
      ) : items.length === 0 ? (
        <EmptyState title="No disclosures yet" body="They appear here as you onboard." />
      ) : (
        <ul className="space-y-4">
          {items.map((d) => (
            <li key={d.type} className="rounded-2xl border border-border bg-surface p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-ink">{d.title}</h2>
                  <p className="mt-0.5 font-mono text-xs text-muted-ink">version {d.version}</p>
                </div>
                {d.acknowledged ? (
                  <span className="inline-flex shrink-0 items-center rounded-full border border-accent/30 bg-accent/10 px-2.5 py-0.5 text-xs font-medium text-accent">
                    Acknowledged
                  </span>
                ) : (
                  <span className="inline-flex shrink-0 items-center rounded-full border border-warning/30 bg-warning/10 px-2.5 py-0.5 text-xs font-medium text-warning">
                    Not yet acknowledged
                  </span>
                )}
              </div>

              <p className="mt-3 text-sm leading-relaxed text-ink">{d.body}</p>

              <p className="mt-4 text-xs text-muted-ink">
                {d.acknowledged && d.acknowledged_at ? (
                  <>Acknowledged on {new Date(d.acknowledged_at).toLocaleDateString()}.</>
                ) : d.acknowledged_version ? (
                  <>
                    You acknowledged version {d.acknowledged_version}. This disclosure has since
                    been updated and will need to be acknowledged again before dependent actions
                    continue.
                  </>
                ) : (
                  <>You will be asked to acknowledge this during verification.</>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
